<script setup>
import { inject, onMounted, shallowRef } from "vue";
import GlAlert from "~/components/gitlab/GlAlert.vue";
import GlKeysetPagination from "~/components/gitlab/GlKeysetPagination.vue";
import GlLoadingIcon from "~/components/gitlab/GlLoadingIcon.vue";
import JobCard from "~/components/JobCard.vue";
import RunCard from "~/components/RunCard.vue";
import PageShell from "~/components/PageShell.vue";
import { useJobActions } from "~/composables/useJobActions";
import { useJobDetail } from "~/composables/useJobDetail";
import { useJobRuns } from "~/composables/useJobRuns";

const { jobId, projectId } = defineProps({
  jobId: { required: true, type: String },
  projectId: { required: true, type: String }
});

const isAdmin = inject("isAdmin", false);

const { job, loading: jobLoading, error: jobError, fetchJobDetail, clearError: clearJobError } = useJobDetail(jobId);

const {
  runs,
  loading: runsLoading,
  error: runsError,
  hasPreviousPage,
  hasNextPage,
  endCursor,
  fetchRuns,
  goNext,
  goPrev,
  clearError: clearRunsError
} = useJobRuns(jobId);

const localJob = shallowRef(null);

const { actionAlert, handleToggle, handleExecute } = useJobActions({
  onExecute: () => {
    localJob.value = { ...localJob.value, lastRunStatus: "running" };
  },
  onToggle: (_, enabled) => {
    localJob.value = { ...localJob.value, enabled };
  }
});

const projectEnabled = shallowRef(true);

const loading = shallowRef(true);

async function loadData() {
  await Promise.all([fetchJobDetail(), fetchRuns()]);
  if (job.value) {
    localJob.value = { ...job.value };
    projectEnabled.value = job.value.projectEnabled ?? true;
  }
  loading.value = false;
}

function clearError() {
  clearJobError();
  clearRunsError();
}

onMounted(loadData);
</script>

<template>
  <PageShell
    :breadcrumbs="[{ text: job?.projectName ?? '…', href: `/projects/${projectId}` }, { text: `Job #${jobId}` }]"
  >
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <GlLoadingIcon size="lg" />
    </div>

    <!-- Error -->
    <GlAlert v-else-if="jobError || runsError" variant="danger" @dismiss="clearError">
      {{ jobError || runsError }}
    </GlAlert>

    <!-- Content -->
    <div v-else-if="localJob && runs">
      <!-- Action alert -->
      <GlAlert v-if="actionAlert" :variant="actionAlert.type" class="mb-4" @dismiss="actionAlert = null">
        {{ actionAlert.message }}
      </GlAlert>

      <!-- Job card (compact — no status/last-executed/recent-runs link) -->
      <div class="mb-6 rounded-lg border border-[var(--gl-border-color-default)]">
        <ul class="list-none p-0">
          <JobCard
            v-if="localJob"
            :job="localJob"
            :is-admin="isAdmin"
            :project-enabled="projectEnabled"
            compact
            @toggle="handleToggle"
            @execute="handleExecute"
          />
        </ul>
      </div>

      <!-- Recent runs -->
      <div class="rounded-lg border border-[var(--gl-border-color-default)]">
        <div
          class="rounded-t-lg border-b border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-subtle)] px-5 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--gl-text-color-subtle)]"
        >
          Recent runs
        </div>

        <!-- Empty state -->
        <div v-if="runs.length === 0" class="px-5 py-8 text-center text-sm text-[var(--gl-text-color-subtle)]">
          No recent runs found for this job.
        </div>

        <!-- Runs list -->
        <ul v-else class="list-none p-0">
          <RunCard v-for="run in runs" :key="run.id" :run="run" :project-id="projectId" :job-id="jobId" />
        </ul>
      </div>

      <!-- Pagination -->
      <GlKeysetPagination
        :has-previous-page="hasPreviousPage"
        :has-next-page="hasNextPage"
        :end-cursor="endCursor"
        :disabled="runsLoading"
        class="mt-4"
        @prev="goPrev"
        @next="goNext"
      />
    </div>
  </PageShell>
</template>
